import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaEnvelope, FaEnvelopeOpen, FaReply, FaTrash, FaEye, 
  FaMapMarkerAlt, FaPhone, FaCalendarAlt, FaUser, FaFilter,
  FaCheckCircle, FaTimesCircle, FaClock
} from 'react-icons/fa';
import { API_URL } from '../../config';

const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, unread: 0, read: 0, replied: 0 });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchBranches();
    fetchStats();
  }, [selectedBranch]);

  const fetchMessages = async () => {
    try {
      setIsLoading(true);
      const url = selectedBranch === 'all' 
        ? `${API_URL}/messages`
        : `${API_URL}/messages?branch=${encodeURIComponent(selectedBranch)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch messages');
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API_URL}/home-content/branches`);
      if (!response.ok) throw new Error('Failed to fetch branches');
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStatusUpdate = async (messageId, newStatus) => {
    try {
      const endpoint = newStatus === 'read' ? 'read' : 'replied';
      const response = await fetch(`${API_URL}/messages/${messageId}/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to update message status');
      
      // Update local state
      setMessages(prev => prev.map(msg => 
        msg._id === messageId 
          ? { ...msg, status: newStatus }
          : msg
      ));
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      console.error('Error updating message status:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      const response = await fetch(`${API_URL}/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete message');
      
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      fetchStats();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
    // Mark as read if unread
    if (message.status === 'unread') {
      handleStatusUpdate(message._id, 'read');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'unread':
        return <FaEnvelope style={{ color: '#3b82f6' }} />;
      case 'read':
        return <FaEnvelopeOpen style={{ color: '#10b981' }} />;
      case 'replied':
        return <FaReply style={{ color: '#8b5cf6' }} />;
      default:
        return <FaEnvelope />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'unread':
        return '#3b82f6';
      case 'read':
        return '#10b981';
      case 'replied':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container>
      <Header>
        <Title>
          <FaEnvelope />
          Messages
        </Title>
        <StatsContainer>
          <StatCard>
            <StatIcon style={{ color: '#3b82f6' }}>
              <FaEnvelope />
            </StatIcon>
            <StatInfo>
              <StatNumber>{stats.total}</StatNumber>
              <StatLabel>Total</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon style={{ color: '#ef4444' }}>
              <FaEnvelope />
            </StatIcon>
            <StatInfo>
              <StatNumber>{stats.unread}</StatNumber>
              <StatLabel>Unread</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon style={{ color: '#10b981' }}>
              <FaEnvelopeOpen />
            </StatIcon>
            <StatInfo>
              <StatNumber>{stats.read}</StatNumber>
              <StatLabel>Read</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon style={{ color: '#8b5cf6' }}>
              <FaReply />
            </StatIcon>
            <StatInfo>
              <StatNumber>{stats.replied}</StatNumber>
              <StatLabel>Replied</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsContainer>
      </Header>

      <FilterSection>
        <FilterLabel>
          <FaFilter /> Filter by Branch:
        </FilterLabel>
        <BranchFilter
          value={selectedBranch}
          onChange={(e) => setSelectedBranch(e.target.value)}
        >
          <option value="all">All Branches</option>
          {branches.map(branch => (
            <option key={branch._id} value={branch.name}>
              {branch.name}
            </option>
          ))}
        </BranchFilter>
      </FilterSection>

      <MessagesContainer>
        {isLoading ? (
          <LoadingMessage>Loading messages...</LoadingMessage>
        ) : messages.length === 0 ? (
          <EmptyState>
            <FaEnvelope style={{ fontSize: '3rem', color: '#9ca3af' }} />
            <p>No messages found for this branch.</p>
          </EmptyState>
        ) : (
          <MessagesGrid>
            {messages.map((message) => (
              <MessageCard
                key={message._id}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
                $status={message.status}
              >
                <MessageHeader>
                  <StatusIndicator $status={message.status}>
                    {getStatusIcon(message.status)}
                  </StatusIndicator>
                  <MessageInfo>
                    <MessageName>{message.name}</MessageName>
                    <MessageEmail>{message.email}</MessageEmail>
                    <MessageBranch>
                      <FaMapMarkerAlt /> {message.branch}
                    </MessageBranch>
                  </MessageInfo>
                  <MessageDate>
                    <FaCalendarAlt />
                    {formatDate(message.createdAt)}
                  </MessageDate>
                </MessageHeader>

                <MessageContent>
                  <MessagePreview>
                    {message.message.length > 100 
                      ? `${message.message.substring(0, 100)}...` 
                      : message.message
                    }
                  </MessagePreview>
                  {message.phone && (
                    <MessagePhone>
                      <FaPhone /> {message.phone}
                    </MessagePhone>
                  )}
                </MessageContent>

                <MessageActions>
                  <ActionButton onClick={() => openMessageModal(message)}>
                    <FaEye /> View
                  </ActionButton>
                  {message.status === 'unread' && (
                    <ActionButton 
                      onClick={() => handleStatusUpdate(message._id, 'read')}
                      $variant="success"
                    >
                      <FaCheckCircle /> Mark Read
                    </ActionButton>
                  )}
                  {message.status !== 'replied' && (
                    <ActionButton 
                      onClick={() => handleStatusUpdate(message._id, 'replied')}
                      $variant="primary"
                    >
                      <FaReply /> Mark Replied
                    </ActionButton>
                  )}
                  <ActionButton 
                    onClick={() => handleDeleteMessage(message._id)}
                    $variant="danger"
                  >
                    <FaTrash /> Delete
                  </ActionButton>
                </MessageActions>
              </MessageCard>
            ))}
          </MessagesGrid>
        )}
      </MessagesContainer>

      {/* Message Modal */}
      {showMessageModal && selectedMessage && (
        <MessageModal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Message from {selectedMessage.name}</ModalTitle>
              <CloseButton onClick={() => setShowMessageModal(false)}>
                <FaTimesCircle />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalSection>
                <SectionTitle>Contact Information</SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <FaUser /> <strong>Name:</strong> {selectedMessage.name}
                  </InfoItem>
                  <InfoItem>
                    <FaEnvelope /> <strong>Email:</strong> {selectedMessage.email}
                  </InfoItem>
                  {selectedMessage.phone && (
                    <InfoItem>
                      <FaPhone /> <strong>Phone:</strong> {selectedMessage.phone}
                    </InfoItem>
                  )}
                  <InfoItem>
                    <FaMapMarkerAlt /> <strong>Branch:</strong> {selectedMessage.branch}
                  </InfoItem>
                  <InfoItem>
                    <FaClock /> <strong>Sent:</strong> {formatDate(selectedMessage.createdAt)}
                  </InfoItem>
                </InfoGrid>
              </ModalSection>
              <ModalSection>
                <SectionTitle>Message</SectionTitle>
                <MessageText>{selectedMessage.message}</MessageText>
              </ModalSection>
            </ModalBody>
            <ModalActions>
              <ActionButton 
                onClick={() => {
                  handleStatusUpdate(selectedMessage._id, 'replied');
                  setShowMessageModal(false);
                }}
                $variant="primary"
              >
                <FaReply /> Mark as Replied
              </ActionButton>
              <ActionButton 
                onClick={() => setShowMessageModal(false)}
                $variant="secondary"
              >
                Close
              </ActionButton>
            </ModalActions>
          </ModalContent>
        </MessageModal>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  font-size: 2rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color}15;
  border-radius: 12px;
`;

const StatInfo = styled.div``;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  background: white;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
`;

const BranchFilter = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}15;
  }
`;

const MessagesContainer = styled.div`
  margin-top: 2rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 1.1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  
  p {
    margin-top: 1rem;
    font-size: 1.1rem;
  }
`;

const MessagesGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const MessageCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => getStatusColor(props.$status)};
  transition: all 0.3s ease;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatusIndicator = styled.div`
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => getStatusColor(props.$status)}15;
  border-radius: 8px;
  flex-shrink: 0;
`;

const MessageInfo = styled.div`
  flex: 1;
`;

const MessageName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const MessageEmail = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const MessageBranch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const MessageDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.8rem;
  white-space: nowrap;
`;

const MessageContent = styled.div`
  margin-bottom: 1.5rem;
`;

const MessagePreview = styled.p`
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.5;
  margin-bottom: 0.75rem;
`;

const MessagePhone = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const MessageActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.$variant) {
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover { background: #059669; }
        `;
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover { background: #2563eb; }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover { background: #dc2626; }
        `;
      case 'secondary':
        return `
          background: #6b7280;
          color: white;
          &:hover { background: #4b5563; }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          &:hover { background: #e5e7eb; }
        `;
    }
  }}
`;

const MessageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${props => props.theme.colors.text.primary};
  
  svg {
    color: ${props => props.theme.colors.primary};
    width: 16px;
  }
`;

const MessageText = styled.div`
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
  line-height: 1.6;
  color: ${props => props.theme.colors.text.primary};
  white-space: pre-wrap;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
`;

const getStatusColor = (status) => {
  switch (status) {
    case 'unread':
      return '#3b82f6';
    case 'read':
      return '#10b981';
    case 'replied':
      return '#8b5cf6';
    default:
      return '#6b7280';
  }
};

export default MessagesPage;
